import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp, CircleNotch, UploadSimple, Briefcase, X } from '@phosphor-icons/react';
import { ChatMessage } from './ChatMessage';
import { CaseStudyCard } from './CaseStudyCard';
import { ExportChatButton } from './ExportChatButton';
import { AssistantMark } from './AssistantMark';
import { ThinkingIndicator } from './ThinkingIndicator';
import { StarterPrompts } from './StarterPrompts';
import { chatWithPortfolio } from '../utils/chatService';
import { analyzeJobDescription } from '../utils/openaiService';
import { getCaseStudyThumbnail } from '../utils/caseStudyMetadata';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePortfolio } from '../context/PortfolioContext';
const CHAT_STORAGE_KEY = 'taio_chat_messages';
const SESSION_KEY = 'taio_chat_session';
const generateSessionId = () => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
const isNewSession = (): boolean => {
  const storedSessionId = sessionStorage.getItem(SESSION_KEY);
  if (!storedSessionId) {
    sessionStorage.setItem(SESSION_KEY, generateSessionId());
    return true;
  }
  return false;
};
interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  caseStudyCards?: Array<{
    slug: string;
    title: string;
    relevance: number;
    description: string;
    thumbnail: string;
  }>;
}
interface ChatInterfaceProps {
  onCaseStudyClick: (slug: string) => void;
  isSplitView: boolean;
  activeCaseStudy?: string | null;
  onClose?: () => void;
}
const getDefaultWelcomeMessage = (): Message[] => [{
  id: '1',
  role: 'assistant',
  content: "Hi! I'm TAIO, Mateo's AI portfolio assistant. Ask me anything about his work, skills, or experience. What would you like to know?",
  timestamp: new Date()
}];
const loadMessagesFromStorage = (): Message[] => {
  if (isNewSession()) {
    localStorage.removeItem(CHAT_STORAGE_KEY);
    return getDefaultWelcomeMessage();
  }
  try {
    const stored = localStorage.getItem(CHAT_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      }));
    }
  } catch (error) {
    console.error('Error loading chat messages from storage:', error);
  }
  return getDefaultWelcomeMessage();
};
const saveMessagesToStorage = (messages: Message[]) => {
  try {
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
  } catch (error) {
    console.error('Error saving chat messages to storage:', error);
  }
};
export function ChatInterface({
  onCaseStudyClick,
  isSplitView,
  activeCaseStudy,
  onClose
}: ChatInterfaceProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    chatEntryPoint,
    setChatEntryPoint,
    closeChatDrawer,
    setPreviousPage
  } = usePortfolio();
  const [messages, setMessages] = useState<Message[]>(loadMessagesFromStorage);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRoleMatchMode, setIsRoleMatchMode] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [lastMessageId, setLastMessageId] = useState<string | null>(null);
  const hasUserMessages = messages.some(m => m.role === 'user');
  useEffect(() => {
    saveMessagesToStorage(messages);
  }, [messages]);
  // Auto-grow the textarea up to a max height.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [input]);
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.id !== lastMessageId) {
      setLastMessageId(lastMessage.id);
      setTimeout(() => {
        if (!messagesContainerRef.current) return;
        if (lastMessage.role === 'user') {
          messagesContainerRef.current.scrollTo({
            top: messagesContainerRef.current.scrollHeight,
            behavior: 'smooth'
          });
        } else {
          const messageElement = document.getElementById(`message-${lastMessage.id}`);
          if (messageElement && messagesContainerRef.current) {
            const container = messagesContainerRef.current;
            const messageTop = messageElement.offsetTop;
            const containerScrollTop = container.scrollTop;
            const containerHeight = container.clientHeight;
            const messageBottom = messageTop + messageElement.clientHeight;
            const isVisible = messageTop >= containerScrollTop && messageBottom <= containerScrollTop + containerHeight;
            if (!isVisible) {
              container.scrollTo({
                top: messageTop - 20,
                behavior: 'smooth'
              });
            }
          }
        }
      }, 100);
    }
  }, [messages, lastMessageId]);
  // Keep the view pinned to the bottom while the thinking indicator shows.
  useEffect(() => {
    if (isLoading && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [isLoading]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        if (input) {
          setInput('');
        } else if (isRoleMatchMode) {
          setIsRoleMatchMode(false);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [input, isRoleMatchMode]);
  const handleClose = () => {
    if (onClose) onClose();
    if (chatEntryPoint && chatEntryPoint !== location.pathname) {
      navigate(chatEntryPoint);
    }
    setChatEntryPoint(null);
    closeChatDrawer();
  };
  const sendMessage = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: trimmed,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    try {
      if (isRoleMatchMode || trimmed.length > 500) {
        const analysisResult = await analyzeJobDescription(trimmed);
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: `I analyzed the job description for "${analysisResult.jobTitle}". Here's how Mateo's experience matches:\n\n${analysisResult.summary}`,
          timestamp: new Date(),
          caseStudyCards: analysisResult.relevantCaseStudies.map(cs => ({
            slug: cs.slug,
            title: cs.title,
            relevance: cs.relevance,
            description: cs.reason,
            thumbnail: getCaseStudyThumbnail(cs.slug)
          }))
        };
        setMessages(prev => [...prev, assistantMessage]);
        setIsRoleMatchMode(false);
      } else {
        const response = await chatWithPortfolio(trimmed, messages, activeCaseStudy);
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: response.message,
          timestamp: new Date(),
          caseStudyCards: response.caseStudyCards
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: "I'm having trouble processing that. Could you try rephrasing?",
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };
  const handleKeyDownInput = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };
  const handleStarterSelect = (prompt: string) => {
    if (prompt === 'Match this to a role') {
      handleRoleMatchClick();
      inputRef.current?.focus();
      return;
    }
    sendMessage(prompt);
  };
  const handleRoleMatchClick = () => {
    const instructionMessage: Message = {
      id: Date.now().toString(),
      role: 'assistant',
      content: "**Role Match Mode**\n\nPaste the job description into the chat, and I'll analyze how Mateo's skills and experience align with the role. I'll highlight relevant case studies and provide a detailed match assessment.",
      timestamp: new Date()
    };
    setMessages(prev => [...prev, instructionMessage]);
    setIsRoleMatchMode(true);
    inputRef.current?.focus();
  };
  const handleUploadJDClick = () => fileInputRef.current?.click();
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsLoading(true);
    setIsRoleMatchMode(true);
    try {
      const text = await file.text();
      const analysisResult = await analyzeJobDescription(text);
      const userMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content: `[Uploaded job description: ${file.name}]`,
        timestamp: new Date()
      };
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `I analyzed the job description for "${analysisResult.jobTitle}". Here's how Mateo's experience matches:\n\n${analysisResult.summary}`,
        timestamp: new Date(),
        caseStudyCards: analysisResult.relevantCaseStudies.map(cs => ({
          slug: cs.slug,
          title: cs.title,
          relevance: cs.relevance,
          description: cs.reason,
          thumbnail: getCaseStudyThumbnail(cs.slug)
        }))
      };
      setMessages(prev => [...prev, userMessage, assistantMessage]);
      setInput('');
      setIsRoleMatchMode(false);
    } catch (error) {
      console.error('File upload error:', error);
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  const handleCaseStudyCardClick = (slug: string) => {
    window.scrollTo(0, 0);
    setPreviousPage(location.pathname);
    onCaseStudyClick(slug);
  };
  const canSend = input.trim().length > 0 && !isLoading;
  return <div className={`flex flex-col h-full bg-black ${isSplitView ? 'hidden lg:flex' : 'flex'}`} role="region" aria-label="Chat interface" style={{
    position: 'relative',
    zIndex: 99999999
  }} data-id="element-156" data-mp="2ce4hmxxh9dj0">
      {/* Mobile-only header */}
      {onClose && <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black flex-shrink-0" data-id="element-157" data-mp="2jyynimbuz58k">
          <div className="flex items-center gap-2.5" data-id="element-158" data-mp="260h0yrxovys8">
            <AssistantMark size={28} data-id="element-159" data-mp="2ayiktf2xw24r" />
            <h2 className="text-white font-medium text-base" data-id="element-160" data-mp="2azlajjoizyj3">Chat with TAIO</h2>
          </div>
          <button onClick={handleClose} className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 active:bg-white/[0.15] transition-colors border border-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30" aria-label="Close chat" data-id="element-161" data-mp="2ynus29epi04f">
            <X className="w-5 h-5 text-white" aria-hidden="true" data-id="element-162" data-mp="2twmi9c9gscqq" />
          </button>
        </div>}

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-4 lg:px-5 py-5 lg:py-7 space-y-6 lg:space-y-7" style={{
      overflowAnchor: 'none',
      scrollBehavior: 'smooth',
      position: 'relative',
      zIndex: 1,
      WebkitOverflowScrolling: 'touch'
    }} role="log" aria-live="polite" aria-atomic="false" data-id="element-163" data-mp="3hm7360p6hk2n">
        {!hasUserMessages ? <div className="h-full flex flex-col items-center justify-center px-2 py-10 text-center" data-id="element-164" data-mp="3mb8411ipe5ll">
            <AssistantMark size={56} data-id="element-165" data-mp="3ia6dd92ur1v8" />
            <h2 className="text-2xl sm:text-3xl font-light text-white mt-6 mb-3" style={{
          fontFamily: "'Gefika', sans-serif"
        }} data-id="element-166" data-mp="3i94p22wtsivk">
              How can I help?
            </h2>
            <p className="text-sm text-white/55 max-w-xs leading-relaxed" data-id="element-167" data-mp="38j1wu4xj38ya">
              I'm TAIO, Mateo's portfolio assistant. Ask about his work, case
              studies, or how his experience maps to a role.
            </p>
            <StarterPrompts onSelect={handleStarterSelect} data-id="element-168" data-mp="2ynqg5h2k7rnw" />
          </div> : <>
            <AnimatePresence initial={false} data-id="element-169" data-mp="2ujsk5f8j6wz2">
              {messages.map(message => <div key={message.id} id={`message-${message.id}`} style={{
            position: 'relative',
            zIndex: 1,
            scrollMarginTop: '20px'
          }} data-id="element-170" data-mp="2pkz5hjhiczxb">
                  <ChatMessage role={message.role} content={message.content} timestamp={message.timestamp} isStreaming={false} data-id="element-171" data-mp="1a1rsjenqbm56" />

                  {message.caseStudyCards && message.caseStudyCards.length > 0 && <div className="mt-4 ml-[40px] space-y-3" style={{
              position: 'relative',
              zIndex: 100,
              pointerEvents: 'auto'
            }} data-id="element-172" data-mp="1j2pm130jkb9i">
                        {message.caseStudyCards.map(card => <CaseStudyCard key={card.slug} slug={card.slug} title={card.title} description={card.description} relevance={card.relevance} thumbnail={card.thumbnail} onClick={() => handleCaseStudyCardClick(card.slug)} data-id="element-173" data-mp="qfbytwmnau2l" />)}
                      </div>}
                </div>)}
            </AnimatePresence>

            <AnimatePresence data-id="element-174" data-mp="2ny5bncrcsg1">
              {isLoading && <ThinkingIndicator roleMatch={isRoleMatchMode} data-id="element-175" data-mp="2mvd9c3p8jyj" />}
            </AnimatePresence>
          </>}

        <div ref={messagesEndRef} data-id="element-176" data-mp="2aahfx0j3yz6x" />
      </div>

      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" accept=".txt,.pdf,.doc,.docx" onChange={handleFileUpload} className="hidden" aria-label="Upload job description file" data-id="element-177" data-mp="2a8ddyo4rsa9a" />

      {/* Composer */}
      <div className="border-t border-white/10 p-3 lg:p-4 flex-shrink-0 bg-black space-y-2.5" data-id="element-178" data-mp="2j42t4ffmk8e8">
        <form onSubmit={handleSubmit} data-id="element-179" data-mp="2eai257cg8eoj">
          <div className={`relative flex flex-col rounded-2xl bg-white/[0.05] border transition-colors ${isRoleMatchMode ? 'border-white/30' : 'border-white/15'} focus-within:border-white/35 focus-within:bg-white/[0.07]`} data-id="element-180" data-mp="2t0pdl341gxfq">
            <label htmlFor="chat-input" className="sr-only" data-id="element-181" data-mp="321xdfpm7e6bx">
              Chat message input
            </label>
            <textarea id="chat-input" ref={inputRef} rows={1} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDownInput} placeholder={isRoleMatchMode ? 'Paste the job description here…' : 'Message TAIO…'} disabled={isLoading} autoComplete="off" className="w-full resize-none bg-transparent px-4 pt-3.5 pb-2 text-white placeholder-white/40 outline-none disabled:opacity-50 leading-relaxed" style={{
            fontSize: '16px',
            maxHeight: '160px'
          }} aria-label={isRoleMatchMode ? 'Paste job description here' : 'Message TAIO'} data-id="element-182" data-mp="2y1df4ipw8wo7" />

            {/* Inline toolbar: compact actions + send */}
            <div className="flex items-center justify-between gap-2 px-2.5 pb-2.5" data-id="element-183" data-mp="op9q3iy6b0ro">
              <div className="flex items-center gap-1" data-id="element-184" data-mp="9ssdypi5ti42">
                <button type="button" onClick={handleRoleMatchClick} className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 ${isRoleMatchMode ? 'bg-white text-black' : 'text-white/70 hover:text-white hover:bg-white/10'}`} aria-label="Role match mode" title="Paste a job description and I'll match Mateo's experience to the role" data-id="element-185" data-mp="etgdy0od99af">
                  <Briefcase className="w-4 h-4" weight="regular" aria-hidden="true" data-id="element-186" data-mp="204gg1bgxxhji" />
                  <span data-id="element-187" data-mp="1xt79902ufs7v">Role Match</span>
                </button>
                <button type="button" onClick={handleUploadJDClick} className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full text-xs font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30" aria-label="Upload job description" title="Upload a .txt, .pdf, or .doc job description for instant analysis" data-id="element-188" data-mp="1tq12zsfqe11e">
                  <UploadSimple className="w-4 h-4" weight="regular" aria-hidden="true" data-id="element-189" data-mp="2chtoz2wdulzm" />
                  <span className="hidden sm:inline" data-id="element-190" data-mp="27pa91za83ern">Upload JD</span>
                </button>
              </div>

              <button type="submit" disabled={!canSend} className="flex-shrink-0 w-9 h-9 bg-white text-black rounded-full flex items-center justify-center transition-all hover:scale-105 active:scale-95 disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:scale-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40" aria-label={isLoading ? 'Sending message' : 'Send message'} data-id="element-191" data-mp="22pxltdffw5oj">
                {isLoading ? <CircleNotch className="w-4 h-4 animate-spin" aria-hidden="true" data-id="element-192" data-mp="37qdyqcibud2a" /> : <ArrowUp className="w-4.5 h-4.5" weight="bold" aria-hidden="true" data-id="element-193" data-mp="3cop8kn8mgnki" />}
              </button>
            </div>
          </div>
        </form>

        {/* Footer hints + export */}
        <div className="flex items-center justify-between gap-3 text-[11px] text-white/35 px-1" data-id="element-194" data-mp="3lqf3i31pq4n1">
          <div className="hidden sm:flex items-center gap-2" aria-hidden="true" data-id="element-195" data-mp="3qnyg6yiri9cv">
            <span data-id="element-196" data-mp="3vfqa9j14mx7r">Enter to send</span>
            <span data-id="element-197" data-mp="3vgrkxme8dhoy">·</span>
            <span data-id="element-198" data-mp="3vhjhazfnjjga">Shift + Enter for new line</span>
          </div>
          <div className="ml-auto" data-id="element-199" data-mp="483yt4861qgc">
            <ExportChatButton messages={messages} data-id="element-200" data-mp="4b88gh4eheee" />
          </div>
        </div>
      </div>
    </div>;
}